import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface AvailabilitySlotCardProps {
  startTime: string;
  endTime: string;
  slotCount: number;
  standardDuration: number; // em minutos
  date: Date;
  onBookSlot?: () => void;
}

export const AvailabilitySlotCard: React.FC<AvailabilitySlotCardProps> = ({
  startTime,
  endTime,
  slotCount,
  standardDuration,
  date,
  onBookSlot
}) => {
  const { t } = useI18n();

  const formatTime = (time: string) => {
    try {
      // Validate time format (HH:mm)
      if (!time || !/^\d{1,2}:\d{2}$/.test(time)) {
        console.warn('Invalid time format:', time);
        return 'Invalid time';
      }
      
      // Create a valid date object
      const [hours, minutes] = time.split(':').map(Number);
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        console.warn('Invalid time values:', { hours, minutes });
        return 'Invalid time';
      }
      
      const date = new Date(2000, 0, 1, hours, minutes);
      return format(date, 'h:mm a');
    } catch (error) {
      console.error('Error formatting time:', time, error);
      return 'Invalid time';
    }
  };

  const getSlotDescription = () => {
    if (slotCount === 1) {
      return `${standardDuration} min slot available`;
    }
    return `${slotCount} x ${standardDuration} min slots available`;
  };

  return (
    <div className="relative rounded-3xl p-4 border-2 border-dashed border-white">
      {/* Slot Count - Top Right */}
      <div className="absolute top-3 right-3">
        <span className="text-white text-xs font-medium">
          {slotCount} {slotCount === 1 ? 'Slot' : 'Slots'}
        </span>
      </div>

      {/* Time Range as Main Title */}
      <div className="text-white font-semibold text-base mb-2">
        {t('calendar.from_to', { start: formatTime(startTime), end: formatTime(endTime) })}
      </div>

      {/* Display of slots */}
      <div className="text-white/60 text-[10px] font-light">
        {t('calendar.available_for_minutes_classes', { minutes: standardDuration })}
      </div>
    </div>
  );
};
